import React from "react";
import { isChunkLoadError, markChunkFailedAgain, mayReloadForChunk, reloadOnceForChunkError } from "@/lib/chunkReload";   // Batch 10 · B16; STEP 10 · C2

/**
 * L1 · Batch C Fix List 2 — WorksheetErrorBoundary.
 *
 * Wraps a worksheet dialog so a runtime crash inside it (e.g. a hooks
 * order regression) does NOT take down the whole page. When it
 * catches, it clears the given URL param so reloading no longer
 * re-opens the broken dialog, and logs to the console.
 */
export default class WorksheetErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null };
  }
  static getDerivedStateFromError(err) {
    if (mayReloadForChunk(err)) return { hasError: false, reloading: true, err };    // Batch 10 · B16
    return { hasError: true, err };
  }
  componentDidCatch(err, info) {
    if (reloadOnceForChunkError(err)) return;      // Batch 10 · B16: a chunk from before a release — reload once
    if (isChunkLoadError(err)) markChunkFailedAgain();   // STEP 10 · C2: not cured by the reload — no "recovered" report
    // eslint-disable-next-line no-console
    console.error("WorksheetErrorBoundary caught:", err, info);
    this.setState({ errorMessage: (err && (err.message || String(err))) || "unknown" });
    // Clear the URL param so a page reload doesn't re-open the crash.
    try {
      const url = new URL(window.location.href);
      const keys = Array.isArray(this.props.clearParams)
        ? this.props.clearParams
        : (this.props.clearParams ? [this.props.clearParams] : []);
      keys.forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.toString());
    } catch (_) { /* noop */ }
  }
  render() {
    if (this.state.reloading) return null;
    if (this.state.hasError) {
      return (
        <div className="fixed inset-x-0 top-2 z-50 mx-auto max-w-lg rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
              role="alert" data-testid="worksheet-error-boundary">
          <div className="font-medium">Worksheet dialog crashed — the page stays open.</div>
          <div className="mt-1 text-xs" data-testid="worksheet-error-message">
            {this.state.errorMessage || "The URL parameter was cleared. Refresh to try again."}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
